import { useState } from 'react';
import { Plus, Search, MoreHorizontal, Edit, Trash, Building2 } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';

import { useToast } from '@/components/ui/Toast';
import { useSuperFunds, useDeleteSuperFund } from '@/hooks/useSuperannuation';
import { SuperFund } from '@/types';
import { formatABN } from '@/lib/utils';

export function SuperFundsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  
  const { toast } = useToast();
  const { data: fundsData, isLoading } = useSuperFunds({
    search: searchQuery,
    page: currentPage,
    limit: pageSize,
  });
  
  const deleteSuperFund = useDeleteSuperFund();

  const funds = fundsData?.data || [];
  const totalItems = fundsData?.meta?.total || 0;

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this super fund?')) return;
    
    try {
      await deleteSuperFund.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Super fund deleted successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete super fund',
        variant: 'destructive',
      });
    }
  };

  const columns: Column<SuperFund>[] = [
    {
      key: 'name',
      header: 'Fund Name',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      key: 'usi',
      header: 'USI',
      sortable: true,
      cell: (row) => row.usi,
    },
    {
      key: 'abn',
      header: 'ABN',
      sortable: true,
      cell: (row) => formatABN(row.abn),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'secondary'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              className="flex items-center cursor-pointer"
              onClick={() => {}}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-destructive focus:text-destructive flex items-center cursor-pointer"
              onClick={() => handleDelete(row.id)}
            >
              <Trash className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Super Funds"
        description="Manage superannuation funds for employee contributions"
        action={{
          label: 'Add Fund',
          onClick: () => {},
          icon: <Plus className="h-4 w-4" />,
        }}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search funds..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={funds}
        columns={columns}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        emptyMessage="No super funds found. Add your first super fund to get started."
      />
    </div>
  );
}
